import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { UploadedChunkObject } from 'src/api/services/MediaClient';

const MAX_PARTS = 1000; // 1000 is the max number of parts allowed by S3
const MIN_PART_SIZE = 6_000_000;

/**
 * Backoff between attempts at the same chunk. Without it, all four attempts at a
 * part were spent within milliseconds of each other, so a brief network blip or
 * a momentary S3 throttle consumed the whole retry budget.
 */
const RETRY_BACKOFF_MS = [1000, 3000, 8000];

/**
 * How long a part may go without any upload progress before it's abandoned and
 * retried. An inactivity limit, not a total one: a large part on a slow uplink
 * can legitimately take minutes, but one that has sent nothing for this long is
 * on a dead connection (dropped Wi-Fi, a sleeping laptop) that the browser
 * would otherwise wait on indefinitely — leaving the file "uploading" forever
 * and the editor's Submit disabled with no error.
 */
const PART_STALL_TIMEOUT_MS = 60_000;

/**
 * Once a part's last byte is sent there are no more progress events, only
 * S3's answer to wait for — slow under load, but not silent for minutes on a
 * live connection.
 */
const PART_RESPONSE_TIMEOUT_MS = 180_000;

/**
 * Parts in flight across every upload on the page, matching the browser's
 * HTTP/1.1 limit of 6 connections per host. Each S3Uploader runs its own pool
 * of workers, so with several files uploading the browser used to queue the
 * extra PUTs itself — invisible to us, and indistinguishable from a stall once
 * the stall timer runs. Queued here instead, a part's timer only starts once
 * its request can actually go out.
 */
const MAX_PARTS_IN_FLIGHT = 6;
let partsInFlight = 0;
const waitingForPartSlot: Array<() => void> = [];

const acquirePartSlot = async (): Promise<void> => {
  if (partsInFlight < MAX_PARTS_IN_FLIGHT) {
    partsInFlight++;
    return;
  }

  // The releasing part hands its slot straight to the next waiter.
  await new Promise<void>((resolve) => waitingForPartSlot.push(resolve));
};

const releasePartSlot = (): void => {
  const next = waitingForPartSlot.shift();

  if (next) {
    next();
  } else {
    partsInFlight--;
  }
};

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    function onAbort() {
      clearTimeout(timer);
      resolve();
    }

    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * An upload that did not deliver every part.
 *
 * This exists because the alternative is worse than a failed upload: S3
 * validates the ETags of the parts you *name* in CompleteMultipartUpload but
 * does not check that you named all of them, so completing with a subset
 * produces a valid, permanently truncated object and a cheerful 200. The file
 * then reports as uploaded and turns out to be unplayable days later.
 */
class IncompleteUploadError extends Error {
  constructor(
    public readonly missingParts: number[],
    public readonly totalParts: number,
    public readonly reason?: unknown,
  ) {
    const detail = (reason as Error)?.message;
    super(
      `Upload incomplete: ${missingParts.length} of ${totalParts} part(s) did not upload` +
        (detail ? ` (${detail})` : ''),
    );
    this.name = 'IncompleteUploadError';
  }
}

const calculateChunks = (fileSize: number): number => {
  const partSize = calculatePartSize(fileSize);
  return Math.min(Math.ceil(fileSize / partSize), MAX_PARTS);
};

const calculatePartSize = (fileSize: number): number => {
  const minPartSize = Math.ceil(fileSize / MAX_PARTS);
  return Math.max(minPartSize, MIN_PART_SIZE);
};

class S3Uploader {
  public readonly concurrentUploads = 6; // Concurrent uploads limit
  public readonly maxUploadAttempts = 3; // Max upload retries per chunk

  private axios: AxiosInstance;
  private abortSignal?: AbortSignal;
  private uploadQueue: number[]; // Part numbers queue
  private uploadedParts: UploadedChunkObject[];
  private uploadPartAttempts: { [key: number]: number }; // Number of upload attempts per chunk
  private firstError: unknown;
  private cancelError: unknown;

  constructor(controller?: AbortController) {
    this.axios = axios.create({
      signal: controller?.signal,
    });
    this.abortSignal = controller?.signal;

    this.uploadQueue = [];
    this.uploadedParts = [];
    this.uploadPartAttempts = {};
    this.firstError = undefined;
    this.cancelError = undefined;
  }

  getChunkBlob = (file: File, start: number, end: number): Blob => file.slice(start, end);

  async upload(
    signedUrls: string[],
    file: File,
    onUploadProgress: (event: any, chunkIndex: number) => void,
    onFail?: (error: unknown) => void,
  ): Promise<UploadedChunkObject[]> {
    const fileSize = file.size;
    const numParts = signedUrls.length;
    const partSize = calculatePartSize(fileSize);

    // Initialize the queue with all part numbers
    this.uploadQueue = Array.from({ length: numParts }, (_, i) => i);
    this.uploadedParts = new Array(numParts);
    this.uploadPartAttempts = {};
    this.firstError = undefined;
    this.cancelError = undefined;

    // A fixed pool of workers, each draining the shared queue. This replaces a
    // 100ms setInterval that watched for "no active uploads and an empty
    // queue": the catch that handled a terminal chunk failure never decremented
    // activeUploads, so that condition could never be satisfied again — the
    // promise never settled, the interval leaked, and the upload sat at 99%
    // with no error and no way forward.
    const workers = Array.from({ length: Math.min(this.concurrentUploads, numParts) }, () =>
      this.drainQueue(signedUrls, file, partSize, onUploadProgress),
    );

    await Promise.all(workers);

    this.uploadPartAttempts = {};

    // Never hand a partial part list to CompleteMultipartUpload. The previous
    // `.filter(part => part !== undefined)` here is what turned a failed chunk
    // into a silently truncated file that reported success.
    const missingParts: number[] = [];
    for (let part = 0; part < numParts; part++) {
      if (!this.uploadedParts[part]) {
        missingParts.push(part + 1);
      }
    }

    if (missingParts.length > 0 || this.firstError || this.cancelError) {
      // A cancellation is the user's own doing, and callers already treat it
      // differently from a broken upload, so let it through as itself rather
      // than relabelling it as an incomplete upload. Checked ahead of any
      // other error, and against the signal too, so an abort is reported as an
      // abort however it interleaved with a failing part.
      const cancelled = this.cancelError ?? (this.abortSignal?.aborted ? new axios.Cancel() : null);

      if (cancelled) {
        onFail?.(cancelled);
        throw cancelled;
      }

      const error = new IncompleteUploadError(missingParts, numParts, this.firstError);
      onFail?.(error);
      throw error;
    }

    return this.uploadedParts;
  }

  private async drainQueue(
    signedUrls: string[],
    file: File,
    partSize: number,
    onUploadProgress: (event: any, chunkIndex: number) => void,
  ): Promise<void> {
    for (;;) {
      const part = this.uploadQueue.shift();

      if (part === undefined) {
        return;
      }

      const attempt = (this.uploadPartAttempts[part] ?? 0) + 1;
      this.uploadPartAttempts[part] = attempt;

      try {
        await this.uploadChunk(signedUrls, file, part, partSize, onUploadProgress);
      } catch (error: any) {
        if (axios.isCancel(error)) {
          // Kept apart from firstError: a cancel that arrives *after* some part
          // had already given up would otherwise be reported as an incomplete
          // upload, because firstError only remembers the first thing to go
          // wrong. What the user did is the more useful answer.
          this.cancelError ??= error;
          // An abort is deliberate and applies to the whole upload — the shared
          // axios signal has already killed every other request in flight.
          this.uploadQueue.length = 0;
          return;
        }

        if (attempt <= this.maxUploadAttempts) {
          await wait(
            RETRY_BACKOFF_MS[Math.min(attempt - 1, RETRY_BACKOFF_MS.length - 1)],
            this.abortSignal,
          );
          this.uploadQueue.push(part);
          continue;
        }

        // Out of attempts. Remember why and keep draining, so the remaining
        // parts still finish and the error names every part that is missing
        // rather than only the first one to give up.
        this.recordError(error);
      }
    }
  }

  private recordError(error: unknown): void {
    if (this.firstError === undefined) {
      this.firstError = error;
    }
  }

  /**
   * PUT one part, abandoning it when it stops making progress. Our own abort
   * surfaces from axios as a cancel; it's re-thrown as an ordinary failure so
   * the part is retried, while a cancel by the user stays a cancel.
   */
  private async putPart(
    signedUrl: string,
    blob: Blob,
    part: number,
    onUploadProgress: (event: any, chunkIndex: number) => void,
  ) {
    // Cancelled while this part waited for a slot or sat in retry backoff.
    if (this.abortSignal?.aborted) {
      throw new axios.Cancel('Upload cancelled');
    }

    // Per-part signal, so a stall aborts only this part. It still follows the
    // upload's own signal, which a per-request signal would otherwise replace.
    const partController = new AbortController();
    const abortPart = () => partController.abort();
    this.abortSignal?.addEventListener('abort', abortPart, { once: true });

    let settled = false;
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const armStallTimer = (ms: number) => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        partController.abort();
      }, ms);
    };

    const options: AxiosRequestConfig = {
      signal: partController.signal,
      onUploadProgress: (event: any) => {
        if (settled) {
          return;
        }

        const sentEverything = event?.total && event.loaded >= event.total;
        armStallTimer(sentEverything ? PART_RESPONSE_TIMEOUT_MS : PART_STALL_TIMEOUT_MS);

        return onUploadProgress(event, part);
      },
    };

    armStallTimer(PART_STALL_TIMEOUT_MS);
    try {
      return await this.axios.put(signedUrl, blob, options);
    } catch (error) {
      if (stalled && !this.abortSignal?.aborted) {
        throw new Error(
          `Part ${part + 1} stalled: no upload progress for ${PART_STALL_TIMEOUT_MS / 1000}s`,
        );
      }

      throw error;
    } finally {
      settled = true;
      clearTimeout(stallTimer);
      this.abortSignal?.removeEventListener('abort', abortPart);
    }
  }

  private async uploadChunk(
    signedUrls: string[],
    file: File,
    part: number,
    partSize: number,
    onUploadProgress: (event: any, chunkIndex: number) => void,
  ) {
    const signedUrl = signedUrls[part];
    const start = part * partSize;
    const end = Math.min((part + 1) * partSize, file.size);

    const blob = this.getChunkBlob(file, start, end);

    await acquirePartSlot();

    let response;
    try {
      response = await this.putPart(signedUrl, blob, part, onUploadProgress);
    } finally {
      releasePartSlot();
    }

    const etag = response.headers.etag;

    // S3 rejects a part list with a missing ETag, but only at completion time
    // and with an opaque validation error. The usual cause is the bucket's CORS
    // config dropping ETag from ExposeHeaders, which is worth naming here rather
    // than debugging from a 422 later.
    if (!etag) {
      throw new Error(
        `S3 did not return an ETag for part ${part + 1} — check the bucket's CORS ExposeHeaders`,
      );
    }

    this.uploadedParts[part] = {
      ETag: etag,
      PartNumber: part + 1,
    };
  }
}

export {
  S3Uploader,
  IncompleteUploadError,
  calculateChunks,
  calculatePartSize,
  PART_STALL_TIMEOUT_MS,
};
